import { 
    API_ENDPOINTS,
    Button,
    customToast,
    FormInput, 
    Permissions, 
    replaceKeyWithValue, 
    route, 
    SelectOption, 
    Sizes,
    Stepper, 
    StringHelpers, 
    Text, 
    useAuthorisationContext, 
    useLangContext, 
    useQueries,
    Variants
} from "carrier-fe";
import { ToshibaInstallationFormData } from "../../pages/installation/toshiba/journey";
import { UseFormReturn } from "../../types/journey";
import { QUERY_KEYS } from "../../util/Constants";
import { useEffect, useState } from "react";
import CreateCustomerModal from "../CreateCustomerModal";

type SelectKey = 'distributors' | 'customers';
type Selects = Record<SelectKey, SelectOption[]>;

function ToshibaStep4({
    form,
    loading,
    refetchStepQueries,
    setRefetchStepQueries
}: {
    form: UseFormReturn<ToshibaInstallationFormData, any>,
    loading?: boolean,
    refetchStepQueries: boolean,
    setRefetchStepQueries: React.Dispatch<React.SetStateAction<boolean>> 
}) {
    const { store, setValues, errors } = form;
    const { hasPermissionAccess } = useAuthorisationContext();
    const { fields, crud } = useLangContext();
    const [selects, setSelects] = useState<Selects>({
        distributors: [],
        customers: [],
    });
    const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);

    const getQueries = () => {
        if (!store?.installer_company_id) return [];
        
        return [
            {
                key: QUERY_KEYS.DISTRIBUTORS,
                config: {
                    method: 'GET',
                    url: route(
                        API_ENDPOINTS.GENERIC.SELECT.DISTRIBUTORS, 
                        { companyId: store?.installer_company_id }
                    )
                },
            },
            {
                key: QUERY_KEYS.CUSTOMERS,
                config: {
                    method: 'GET',
                    url: route(
                        API_ENDPOINTS.GENERIC.SELECT.CUSTOMERS, 
                        { company_id: store?.installer_company_id }
                    ),
                },
            }
        ];
    };

    const { 
        data: selectOptions, 
        error, 
        isLoading,
        refetch,
        refetching
    } = useQueries<{ data: SelectOption[] }>(getQueries());

    useEffect(() => {
        if (!store?.installer_company_id || refetching) return;

        setValues({ distributor_id: '' });
        refetch();
    }, [store?.installer_company_id]);

    useEffect(() => {
        if (!refetchStepQueries) return;

        refetch([QUERY_KEYS.CUSTOMERS])
        .then(() => {
            if (!refetching) setRefetchStepQueries(refetching);
        });
    }, [refetchStepQueries, refetching]);
    
    useEffect(() => {
        if (!selectOptions) return;
        
        setSelects((prev) => ({
            ...prev,
            distributors: selectOptions.distributors?.data ?? [],
            customers: selectOptions.customer_companies?.data ?? [],
        }))

        if (selectOptions.distributors?.data.length === 1) {
            setValues({
                distributor_id: selectOptions?.distributors?.data[0].value
            });
        }
    }, [selectOptions]);

    useEffect(() => {
        if (selects.distributors.length === 1 && !store?.distributor_id) {
            setValues({
                distributor_id: selects.distributors[0].value
            });
        }
    }, [store?.distributor_id, selects.distributors]);

    useEffect(() => {
        if (!error) return;
        
        Object.values(error).map((e) =>
            customToast({ 
                title: (e?.response?.data as any)?.message, 
                variant: Variants.Danger 
            })
        );
    }, [error]);

    return (
        <Stepper.Step
            loading={isLoading || loading}
            title={
                `${fields?.commissioning 
                    || 'Commissioning'}
                ${crud?.sub_titles?.details 
                    || 'Details'
                }`
            }
        >
            <FormInput
                type='datetime'
                name='commissioning_date'
                label={StringHelpers.title(
                    fields?.event_at
                    || 'date'
                )}
                value={store?.commissioning_date}
                onChange={value => {
                    if (typeof value !== 'string') return;

                    setValues({
                        commissioning_date: value
                    })
                }}
                errorMessages={errors?.commissioning_date}
            />

            <FormInput
                name='distributor_id'
                type='searchable-select'
                label={StringHelpers.title(
                    fields?.distributor_ids 
                    || 'Distributors'
                )}
                value={store?.distributor_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;

                    setValues({
                        distributor_id: value
                    })
                }}
                options={selects.distributors}
                clearable={false}
                errorMessages={errors?.distributor_id}
            />
            
            <FormInput
                name='customer_id'
                type='searchable-select'
                label={StringHelpers.title(
                    crud?.customer 
                    || 'Customer'
                )}
                value={store?.customer_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    
                    setValues({
                        customer_id: value
                    })
                }}
                options={selects.customers}
                clearable={false}
                errorMessages={errors?.customer_id}
            />

            {hasPermissionAccess(Permissions.CUSTOMER_STORE) &&
                <div>
                    <Text className='text-muted'>
                        {crud?.helpers?.customer_not_in_list ||
                            'If the customer is not appearing in the list and is a new customer, please click the button below to create a new customer record.'
                        }
                    </Text>

                    <Button
                        label={replaceKeyWithValue(
                            crud?.pages?.create?.title
                                || 'Create',
                            'model',
                            crud?.models?.customer
                                || 'Customer',
                        )}
                        size={Sizes.Small}
                        onClick={() => setShowCreateCustomerModal(true)}
                    />
                </div>
            }

            <CreateCustomerModal
                open={showCreateCustomerModal}
                onClose={() => setShowCreateCustomerModal(false)}
                installerCompanyId={store?.installer_company_id}
                onCustomerCreated={(customer) => {
                    setValues({ customer_id: customer.id });
                    refetch([QUERY_KEYS.CUSTOMERS]);
                }}
            />
        </Stepper.Step>
    )
};

export default ToshibaStep4;